import { findProjectType, projectTypeLabel } from "./projectTypes";

// "LOS SANTOS - FEATURE FILM" (Figma 21). The breakdown name often already
// carries the project type as a prefix ("Feature Film - Los Santos", which is
// what the blue header shows), and appending the type again printed
// "FEATURE FILM - LOS SANTOS - FEATURE FILM". So the prefix is peeled off
// first, and the type is only appended when it is not already in the name.
//
// The type is printed in the page's language ("LOS SANTOS - LONG MÉTRAGE"),
// but casting writes the name in theirs, so every spelling of the type counts
// when looking for it in the name.
export const projectLine = (breakdownName: string, projectType?: string): string => {
  const name = (breakdownName || "").trim();
  const type = (projectType || "").trim();
  if (!type) return name.toUpperCase();
  const label = projectTypeLabel(type);
  const known = findProjectType(type);
  const spellings = [type, label, ...(known ? [known.en, known.fr, known.es, ...(known.aliases || [])] : [])]
    .filter((s, i, all) => s && all.indexOf(s) === i)
    // Longest first, so "Music Video / Filmclip" is peeled whole, not as "Music Video".
    .sort((a, b) => b.length - a.length);

  // A prefix only counts when a separator (or the end) follows it, so
  // "Pilotes du Nord" keeps its name when the type is "Pilot".
  const prefixed = (s: string) => new RegExp(`^${escape(s)}\\s*([-–—:]|$)`, "i").test(name);
  const prefix = spellings.find(prefixed);
  // "Feature Film - Los Santos" -> "Los Santos"
  const bare = prefix ? name.slice(prefix.length).replace(/^\s*[-–—:]\s*/, "").trim() || name : name;

  // Already named only as a whole word, in the spellings this page would print:
  // "The Playhouse" still gets "- PLAY".
  const alreadyNamed = [type, label].some((s) => containsWord(bare, s));
  return (alreadyNamed ? bare : `${bare} - ${label}`).toUpperCase();
};

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Letters and digits, accented ones included ("é" is not a word character to \b).
const WORD_CHAR = /[a-z0-9À-ɏ]/i;

const containsWord = (haystack: string, needle: string): boolean => {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  for (let at = h.indexOf(n); at >= 0; at = h.indexOf(n, at + 1)) {
    const before = at === 0 ? "" : h[at - 1];
    const after = h[at + n.length] || "";
    if (!WORD_CHAR.test(before) && !WORD_CHAR.test(after)) return true;
  }
  return false;
};
